
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface CourseReviewModalProps {
  courseId: string;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onReviewSubmitted?: () => void;
}

export const CourseReviewModal = ({ 
  courseId, 
  userId,
  isOpen, 
  onClose,
  onReviewSubmitted
}: CourseReviewModalProps) => {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const queryClient = useQueryClient();
  
  useEffect(() => {
    const getAccessToken = async () => {
      const { data } = await supabase.auth.getSession();
      setAccessToken(data.session?.access_token || null);
    };
    
    getAccessToken();
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setRating(0);
      setReview('');
      
      // Fetch existing review if available
      const fetchExistingReview = async () => {
        try {
          const { data, error } = await supabase
            .from('course_reviews')
            .select('rating, review')
            .eq('user_id', userId)
            .eq('course_id', courseId)
            .limit(1);
            
          if (error) {
            console.error("Error fetching existing review:", error);
            return;
          }
          
          if (data && data.length > 0) {
            setRating(data[0].rating || 0);
            setReview(data[0].review || '');
            console.log("Loaded existing review:", data[0]);
          }
        } catch (error) {
          console.error("Failed to load existing review:", error);
        }
      };
      
      fetchExistingReview();
    }
  }, [isOpen, courseId, userId]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!accessToken) {
      toast.error('You need to be logged in to submit a review');
      return;
    }

    try {
      setIsSubmitting(true);
      
      console.log('Submitting review:', {
        user_id: userId,
        course_id: courseId,
        rating,
        review
      });

      const response = await fetch('https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          path: "submit_review",
          params: {
            user_id: userId,
            course_id: courseId,
            rating,
            review
          }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        
        // Check if it's a unique constraint violation
        if (errorText.includes('unique_user_course_review')) {
          throw new Error("You have already reviewed this course");
        }
        
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      console.log('Review submitted successfully:', data);
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['user-review', courseId, userId] });
      queryClient.invalidateQueries({ queryKey: ['coursePlayer', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-featured-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-average-rating', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-review-count', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-reviews', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course-review-exists', courseId, userId] });
      
      // Call the callback to notify parent component
      if (onReviewSubmitted) {
        onReviewSubmitted();
      } else {
        // Default close action if no callback provided
        onClose();
      }
      
    } catch (error: any) {
      console.error('Error submitting review:', error);
      toast.error(error.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`h-8 w-8 cursor-pointer 
          ${(hoveredRating || rating) >= star 
            ? 'text-yellow-500 fill-yellow-500' 
            : 'text-gray-300'}`}
        onMouseEnter={() => setHoveredRating(star)}
        onMouseLeave={() => setHoveredRating(0)}
        onClick={() => setRating(star)}
      />
    ));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rate This Course</DialogTitle>
          <DialogDescription>
            Share your experience with this course to help other students.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex justify-center space-x-2 my-4">
          {renderStars()}
        </div>
        
        <Textarea
          placeholder="Write your review (optional)"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          className="min-h-[100px]"
        />
        
        <Button 
          onClick={handleSubmit} 
          className="w-full mt-4"
          disabled={rating === 0 || isSubmitting || !accessToken}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
